<template>
    <v-card class="mx-2 mt-2 mb-2 d-flex flex-column flex-grow-1" style="min-height: 0;">
        <v-toolbar density="compact">
            <v-toolbar-title
                :class="
                    $vuetify.display.mdAndUp ? 'text-h6' : 'text-subtitle-1'
                "
                >{{ displayTitle }}</v-toolbar-title
            >
            <v-spacer v-if="showApplyToOtherUnits" />
            <v-tooltip v-if="showApplyToOtherUnits" :text="applyToOtherUnitsTooltip" location="bottom">
                <template #activator="{ props: tooltipProps }">
                    <v-btn
                        v-bind="tooltipProps"
                        icon
                
                        variant="text"
						color="primary"
                        @click="$emit('apply-to-other-units')"
                    >
                        <v-icon size="small">mdi-arrow-collapse-right</v-icon>
                    </v-btn>
                </template>
            </v-tooltip>
        </v-toolbar>
        <v-card-text
            class="d-flex flex-wrap flex-grow-1"
        >
            <!-- Insulation Details-->
            <v-col cols="12" :md="mdSettingsInsulation">
                <v-select
                    :model-value="formData.insulationType"
                    @update:modelValue="(v) => updateField('insulationType', v)"
                    :items="insulationTypes"
                    item-title="title"
                    item-value="value"
                    return-object
                    label="Tip izolație"
                    variant="underlined"
                    hide-details
                    :dirty="false"
                />
            </v-col>

            <!--  Tip folie Tacker -->
            <v-col
                cols="12"
                v-if="formData.insulationType?.value?.subtype === 2"
                :md="mdSettingsInsulation"
            >
                <v-select
                    :model-value="formData.tackerFoil"
                    @update:modelValue="(v) => updateField('tackerFoil', v)"
                    :items="tackerFoilTypes"
                    item-title="title"
                    item-value="value"
                    return-object
                    label="Tip folie tacker"
                    variant="underlined"
                    hide-details
                />
            </v-col>

            <!--  Izolație suplimentară -->
            <v-col
                cols="12"
                :md="mdSettingsInsulation"
                v-for="(ins, i) in formData.additionalInsulations"
                :key="`add-ins-${i}-${ins?.value ?? 'null'}`"
            >
                <div class="d-flex align-center pa-0 mt-0">
                    <v-select
                        :model-value="ins"
                        :items="additionalInsulationTypes"
                        item-title="title"
                        item-value="value"
                        return-object
                        label="Izolație suplimentară"
                        variant="underlined"
                        hide-details
                        @update:modelValue="
                            (v) => updateAdditionalInsulationAt(i, v)
                        "
                        :dirty="false"
                    />
                    <v-btn
                        icon
                        size="small"
                        variant="text"
                        @click="removeAdditionalInsulationAt(i)"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-col>
            <!-- Existing Product Details-->
            <FormSelectField
                :key="pipeSelectKey"
                label="Țeavă PE-Xa (diametru)"
                :items="filteredPipeChoices"
                v-model="formData.pipeDiameter"
                @update:modelValue="emitUpdate"
                :mdSettings="mdSettings"
            />

            <FormSelectField
                label="Distribuitor"
                :items="filteredDistributorChoices"
                v-model="formData.distributor"
                @update:modelValue="emitUpdate"
                :mdSettings="mdSettings"
            />
            <FormSelectField
                label="Cutie metalică pentru distribuitor"
                :items="metalBoxChoices"
                v-model="formData.metalBox"
                @update:modelValue="emitUpdate"
                :mdSettings="mdSettings"
            />
            <FormSelectField
                label="Reglare temperatură"
                :items="temperatureControlChoices"
                v-model="formData.temperatureRegulation"
                @update:modelValue="emitUpdate"
                :mdSettings="mdSettings"
            />
            <FormSelectField
                label="Automatizare"
                :items="automationChoices"
                :model-value="formData.automation"
                @update:modelValue="(v) => updateField('automation', v)"
                :mdSettings="mdSettings"
            />
            <FormSelectField
                label="Plasă de armare"
                :items="reinforcementMeshChoices"
                v-model="formData.reinforcementMesh"
                @update:modelValue="emitUpdate"
                :mdSettings="mdSettings"
            />

            <v-col cols="12" class="d-flex justify-end"
                ><v-btn variant="text" @click="addAdditionalInsulationRow">
                    <v-icon color="primary">mdi-plus</v-icon>
                    izolație suplimentară
                </v-btn></v-col
            >
          
        </v-card-text>
    </v-card>
</template>

<script>
import FormSelectField from "@/components/offer/FormSelectField.vue";
import { mapGetters } from "vuex";
import { showAlertModal } from "@/utils/utils";
import {
    PIPE_CHOICES,
    DISTRIBUTION_CHOICES,
    BOX_FOR_DISTRIBUTION_CHOICES,
    AUTOMATION_CHOICES,
    NO_YES_CHOICES,
    TEMPERATURE_CONTROL_CHOICES
} from "@/IPA/ProductChoices.js";

const toNum = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);

export default {
    name: "ProductUnitDetails",
    components: {
        FormSelectField
    },
    emits: ["update", "apply-to-other-units"],
    props: {
        initialData: { type: Object, default: () => ({}) },
        location: { type: Object, required: true },
        /** Effective compartmentation (from offer with fallback); use instead of location.compartmentation. */
        effectiveCompartmentation: { type: Number, default: 0 },
        /** Effective customization (from offer with fallback); use instead of location.customization. */
        effectiveCustomization: { type: Number, default: 0 },
        title: { type: [String, Number], default: "" },
        insulationTypes: { type: Array, default: () => [] },
        tackerFoilTypes: { type: Array, default: () => [] },
        additionalInsulationTypes: { type: Array, default: () => [] },
        /** When true, show button to copy this unit's settings to all other units (etaje/apartamente). */
        showApplyToOtherUnits: { type: Boolean, default: false },
        /** Label for the apply-to-other-units button (e.g. "Aplică pe celelalte etaje"). */
        applyToOtherUnitsLabel: { type: String, default: "Aplică pe celelalte nivele" },
        /** Tooltip for the apply-to-other-units button (e.g. "Aplică pe toate etajele"). */
        applyToOtherUnitsTooltip: { type: String, default: "Aplică pe toate nivelele" }
    },
    data() {
        const defaultAdditionalInsulation =
            this.additionalInsulationTypes.find((i) => i.value === 0) ?? null;
        const initial = this.initialData || {};
        const pickNumeric = (val) => {
            if (val === undefined || val === null) return undefined;
            if (typeof val === "object") {
                if ("value" in val && val.value !== undefined) return val.value;
                if ("id" in val && val.id !== undefined) return val.id;
            }
            return val;
        };
        const normalizeNumber = (primary, fallback, defaultValue) => {
            const direct = pickNumeric(primary);
            if (direct !== undefined) return direct;
            const secondary = pickNumeric(fallback);
            if (secondary !== undefined) return secondary;
            return defaultValue;
        };
        const initialAdditional =
            initial.additionalInsulations ?? initial.additional_insulations;
        const safeAdditional = Array.isArray(initialAdditional)
            ? initialAdditional.length
                ? initialAdditional
                : [defaultAdditionalInsulation].filter(Boolean)
            : [defaultAdditionalInsulation].filter(Boolean);

        return {

            automationProducts: [],
            automationQuantities: {},
            showAdditionalInsulation: false,
            isSyncFromParent: false,

            formData: {
                pipeDiameter: normalizeNumber(
                    initial.pipeDiameter,
                    initial.pipe_diameter,
                    17
                ), // 17mm default
                distributor: normalizeNumber(
                    initial.distributor,
                    initial.distributor_id,

                    2
                ), // Modular
                metalBox: normalizeNumber(
                    initial.metalBox,
                    initial.metal_box,
                    2
                ), // Sistema
                temperatureRegulation: normalizeNumber(
                    initial.temperatureRegulation,
                    initial.temperature_regulation,
                    1
                ), // Nu
                automation: normalizeNumber(
                    initial.automation,
                    initial.automation_id,
                    1
                ), // Nu
                reinforcementMesh: normalizeNumber(
                    initial.reinforcementMesh,
                    initial.reinforcement_mesh,
                    2
                ), // Da
                insulationType:
                    initial.insulationType ?? initial.insulation_type ?? null,
                tackerFoil:
                    initial.tackerFoil ??
                    initial.tacker_foil ??
                    this.tackerFoilTypes?.[1] ??
                    null,
                additionalInsulations: safeAdditional
            },
            pipeChoices: PIPE_CHOICES,
            distributorChoices: DISTRIBUTION_CHOICES,
            metalBoxChoices: BOX_FOR_DISTRIBUTION_CHOICES,
            automationChoices: AUTOMATION_CHOICES,
            reinforcementMeshChoices: NO_YES_CHOICES,
            temperatureControlChoices: TEMPERATURE_CONTROL_CHOICES
        };
    },
    mounted() {
        this.normalizeInsulationType();
        this.enforcePipeRuleForCurrentInsulation();
    },

    computed: {
        ...mapGetters(["userPermissions"]),
        mdSettingsInsulation() {
            const cust = Number(this.effectiveCustomization ?? this.location?.customization ?? 0);
            return cust !== 0 ? "12" : "4";
        },
        mdSettings() {
            const cust = Number(this.effectiveCustomization ?? this.location?.customization ?? 0);
            return cust !== 0 ? "6" : "4";
        },
        /** Title by customization: 0 = Întreaga locație, 1 = floor name, 2 = apartment name. */
        displayTitle() {
            const cust = Number(this.effectiveCustomization ?? this.location?.customization ?? 0);
            if (cust === 0) return "Întreaga locație";
            return this.title ?? "";
        },
        pipeSelectKey() {
            return `pipe-${this.insulationSubtype}`;
        },
        insulationSubtype() {
            const it = this.formData.insulationType;
            return Number(it?.value?.subtype ?? it?.subtype ?? null);
        },
        filteredPipeChoices() {
            if (this.insulationSubtype !== 1 && this.insulationSubtype !== 3) return this.pipeChoices 
            return this.pipeChoices.filter((p) => Number(p.value) !== 20);
        },
      
       
       
        canAddMore() {
            const arr = this.formData.additionalInsulations || [];
            if (!arr.length) return true;
            const last = arr[arr.length - 1];
            return (
                !!last &&
                (typeof last === "object"
                    ? last.value || last.title || last.label
                    : true)
            );
        },
        filteredDistributorChoices() {
            if (
                this.userPermissions?.ipaOfferManagement?.canViewRRDistribuitor
            ) {
                return this.distributorChoices;
            }

            return this.distributorChoices.filter((d) => d.value !== 2);
        },
       
    },

    methods: {
        /**
         * Normalizes formData.insulationType to a full option object from insulationTypes.
         * If current value is already normalized, does nothing.
         */
        normalizeInsulationType() {
            const current = this.formData.insulationType;
            if (!current) return;

            // Already a full option (has title + value)
            if (current.title && current.value) return;

            // Current is raw value object { subtype, size, type }
            const st = current.subtype;
            const sz = current.size;

            const match = (this.insulationTypes || []).find(
                (opt) =>
                    opt?.value?.subtype === st &&
                    (sz == null || opt?.value?.size === sz)
            );

            if (match) {
                this.formData.insulationType = match;
            }
        },

        /**
         * Enforces the pipe diameter rule for current insulation selection.
         * If GROOVED and pipe is 20, reset to 17.
         */
        enforcePipeRuleForCurrentInsulation() {
            if (
                (this.insulationSubtype === 1 || this.insulationSubtype === 3) &&
                Number(this.formData.pipeDiameter) === 20
            ) {
                this.formData.pipeDiameter = 17;
                this.emitUpdate();
            }
        },
     
       

        /**
         * Emits local form changes and (unless syncing from parent) patches all rooms, then emits an updated location.
         *
         * @returns {void}
         *
         * @fires update - With the raw `formData` payload.
         * @fires update-location - With `{ ...location, rooms: patchedRooms, isLocationChanged: true }`.
         *
         * @description
         * - Always emits `"update"` with current `this.formData`.
         * - If `this.isSyncFromParent` is true, exits early to avoid an update loop.
         * - If there are no rooms, exits (nothing to patch).
         * - Builds a numeric patch from form fields using `toNum(...)`.
         * - Applies the patch to every room, sets `isLocationChanged: true`,
         *   and emits `"update-location"` with the updated location object.
         *
         * @example
         * // Parent can listen to incremental form changes:
         * <Child @update="onFormUpdate" @update-location="onLocationUpdate" />
         */
        emitUpdate() {
            this.$emit("update", {
                ...this.formData,
                pipe_diameter: toNum(this.formData.pipeDiameter),
                distributor: toNum(this.formData.distributor),
                metal_box: toNum(this.formData.metalBox),
                temperature_regulation: toNum(
                    this.formData.temperatureRegulation
                ),
                automation: toNum(
                    this.formData.automation?.value ?? this.formData.automation
                ),
                reinforcement_mesh: toNum(this.formData.reinforcementMesh),
                insulation_type: this.formData.insulationType,
                tacker_foil: this.formData.tackerFoil,
                additional_insulations: this.formData.additionalInsulations
            });
        },
        updateField(field, value) {
            this.formData[field] = value;

            if (field === "insulationType") {
                const subtype = Number(
                    value?.value?.subtype ?? value?.subtype ?? null
                );
                if (
                    (subtype === 1 || subtype === 3) &&
                    Number(this.formData.pipeDiameter) === 20
                ) {
                    this.formData.pipeDiameter = 17;
                }
            }

            
            this.emitUpdate();
        },

        addAdditionalInsulationRow() {
            const arr = Array.isArray(this.formData.additionalInsulations)
                ? [...this.formData.additionalInsulations]
                : [];

            const defaultInsulation =
                this.additionalInsulationTypes.find((i) => i.value === 0) ??
                null;

            const last = arr[arr.length - 1];
            if (last?.value === 0) {
                showAlertModal(
                    this.$store,
                    "Completează izolația suplimentară existentă înainte de a adăuga alta.",
                    "warning",
                    10000
                );
                return;
            }

            arr.push(defaultInsulation);
            this.updateField("additionalInsulations", arr);
        },

        updateAdditionalInsulationAt(i, val) {
            const arr = [...(this.formData.additionalInsulations || [])];
            arr[i] = val;
            this.updateField("additionalInsulations", arr);
        },

        removeAdditionalInsulationAt(i) {
            const arr = [...(this.formData.additionalInsulations || [])];
            arr.splice(i, 1);
            this.updateField("additionalInsulations", arr);
        }
    },
    watch: {
        initialData: {
            deep: true,
            handler(newVal) {
                const incoming = Array.isArray(newVal?.additionalInsulations)
                    ? newVal.additionalInsulations
                    : [];
                const current = Array.isArray(
                    this.formData.additionalInsulations
                )
                    ? this.formData.additionalInsulations
                    : [];

                const safeAdditional =
                    incoming.length >= current.length ? incoming : current;

				const currentTitle =
					this.formData.insulationType?.title ??
					this.formData.insulation_type?.title;

				const newTitle =
					newVal?.insulationType?.title ??
					newVal?.insulation_type?.title;

                const tackerFoil =
				currentTitle !== newTitle
					? this.tackerFoilTypes[1]
					: newVal?.tackerFoil ?? 
					this.formData.tackerFoil ??
					this.tackerFoilTypes?.[1];  

                // Let incoming initialData from parent take precedence so "Aplică pe celelalte etaje" works
                this.formData = {
                    ...this.formData,
                    ...newVal,
                    tackerFoil,
                    additionalInsulations: safeAdditional
                };
            }
        },
       
       
    }
};
</script>
<style scoped></style>
